import { useCallback, useEffect, useState } from 'react';

import { PromocodesOfferType } from '@occc/graphql';
import { OfferType } from '@occc-app/offerValidationService/types';

// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  PromocodesGlobalTable,
  PromocodesIndividualTable,
  routeCreator as promocodesRouterCreator,
} from '@occc/feat-promocodes';

import { Divider } from '@mui/material';
import { SectionCard } from '../../components';
import { Offer } from '../../component-details.utils';

type PromocodesSectionProps = {
  offerType: OfferType | null;
  campaignUuid: string;
  offerId: string;
  earningMechanism?: Offer['earningMechanism'];
  isActionsEnabled: boolean;
  isDownloadEnabled: boolean;
};

const mapPromoCodeGroups = (earningMechanism?: Offer['earningMechanism']) => {
  if (earningMechanism?.__typename !== 'EarningMechanismPromocodes') {
    return [];
  }

  return earningMechanism.promoCodeGroups.edges.map(({ node }) => node);
};

export const PromocodesSection = ({
  offerType,
  campaignUuid,
  offerId,
  earningMechanism,
  isActionsEnabled,
  isDownloadEnabled,
}: PromocodesSectionProps) => {
  const [promoCodeGroups, setPromoCodeGroups] = useState(() =>
    mapPromoCodeGroups(earningMechanism),
  );

  useEffect(() => {
    setPromoCodeGroups(mapPromoCodeGroups(earningMechanism));
  }, [earningMechanism]);

  const handleGroupDeleted = useCallback((groupId: string) => {
    setPromoCodeGroups((groups) => groups.filter((group) => group.id !== groupId));
  }, []);

  if (
    offerType !== OfferType.Promocodes ||
    earningMechanism?.__typename !== 'EarningMechanismPromocodes'
  ) {
    return null;
  }

  return (
    <SectionCard
      title='Promo Codes'
      sx={{ mt: 1 }}
      contentSx={{ px: { xs: 0, md: 0 }, pb: { xs: 0, md: 0 }, pt: 2, gap: 0 }}
    >
      <Divider sx={{ my: 0 }} />
      {earningMechanism.codesType === PromocodesOfferType.Global ? (
        <PromocodesGlobalTable
          promocodes={earningMechanism.promocodes}
          isActionsEnabled={isActionsEnabled}
          getPromocodeDetailsUrl={(promocodeId) =>
            promocodesRouterCreator.promocodeDetails(campaignUuid, offerId, promocodeId)
          }
        />
      ) : (
        <PromocodesIndividualTable
          promoCodeGroups={promoCodeGroups}
          isDownloadEnabled={isDownloadEnabled}
          isDeleteEnabled={isActionsEnabled}
          onGroupDeleted={handleGroupDeleted}
        />
      )}
    </SectionCard>
  );
};
